import { useCallback } from 'react';
import { StackActions } from '@react-navigation/native';
import { useAppDispatch, useAppStore } from 'store/hooks';
import Sentry from 'utils/sentry';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { navigationRef } from 'utils/navigationv6';
import { loanTypeReasonValue } from '../models';
import { prefetchMonevoData } from '../prefetch';
import { setMonevoDetails, startMonevoOnboarding, } from '../reducers/monevoOnboarding';
import { findMonevoStepIndex, findNextMonevoStep, getMonevoStepId, selectMonevoStepContext, } from '../steps';
/**
 * Single owner of "where does the user go next" for the Monevo flow. Screens
 * never name their successor — they hand back their step id and the table in
 * `steps.ts` decides, so a screen that is already answered is skipped and a
 * review screen is shown every time it is walked past.
 */
const useMonevoNavigation = () => {
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const navigation = useMainStackNavigation();
    const navigateToNextStep = useCallback((fromStepId) => {
        const ctx = selectMonevoStepContext(store.getState());
        const navState = navigation.getState() ?? navigationRef.getRootState();
        if (!navState)
            return;
        const currentRoute = navState.routes[navState.index];
        /**
         * The tag the step was pushed with wins over the route name, which only
         * ever resolves to the first position a route holds in the table — an
         * AddressPostCodeScreen opened for the previous address would otherwise
         * resolve as the primary one and send the user round again.
         */
        const from = fromStepId ??
            getMonevoStepId(currentRoute?.params) ??
            currentRoute?.name ??
            '';
        const fromIndex = findMonevoStepIndex(from);
        const next = findNextMonevoStep(ctx, fromIndex);
        if (!next)
            return;
        const params = {
            ...next.params?.({
                ctx,
                dispatch,
                navigation,
                next: () => navigateToNextStep(next.id),
            }),
            /**
             * Tags the instance so the stack can be searched by step rather than
             * by route name — AddressDateScreen and MonevoResidentType each sit at
             * two positions in the table, for the current and previous address.
             */
            monevoStepId: next.id,
        };
        /**
         * `navigate` is no use here: it only reuses the route it is already on,
         * so moving between two steps that share a screen would replace it
         * rather than open a second one, and resolving back to a review screen
         * would stack a duplicate on top of the one already below.
         */
        const existingIndex = navState.routes.findLastIndex((route) => route.name === next.route && getMonevoStepId(route.params) === next.id);
        if (existingIndex > -1 && existingIndex < navState.index) {
            navigation.dispatch(StackActions.pop(navState.index - existingIndex));
            return;
        }
        // The table is heterogeneous, so params cannot be narrowed per route.
        navigation.dispatch(StackActions.push(next.route, params));
    }, []);
    /**
     * Entry point for every call site outside the flow. Resets any answers left
     * over from a previous attempt, then resolves from the top of the table.
     *
     * `details` carries what the entry resolver worked out before the first
     * navigation — the reuse flag and the prefill — so the loan amount screen
     * renders in its reuse state on first paint rather than a moment later.
     *
     * A reusing run skips the prefetch: it would seed defaults and estimates
     * the user never confirmed, which the submission would then save and quote
     * on. `continueIntoFunnel` fetches them if the reuse is rejected.
     */
    const startMonevoFlow = useCallback((type, details) => {
        dispatch(startMonevoOnboarding({
            type,
            loanPurpose: type ? loanTypeReasonValue[type] : undefined,
            ...details,
        }));
        if (!details?.reuseDetails) {
            prefetchMonevoData(dispatch, store.getState).catch((error) => Sentry.captureException(error));
        }
        navigateToNextStep('');
    }, [navigateToNextStep]);
    /**
     * A reused payload was rejected for something the funnel collects. Clears
     * the reuse flag for the rest of the run so the two paths cannot bounce,
     * asks for the estimates the flow would have fetched on entry, and carries
     * on from the step the user is standing on — anything already answered,
     * including the amount they just typed, is kept.
     */
    const continueIntoFunnel = useCallback((fromStepId) => {
        dispatch(setMonevoDetails({ reuseDetails: false }));
        prefetchMonevoData(dispatch, store.getState).catch((error) => Sentry.captureException(error));
        navigateToNextStep(fromStepId);
    }, [navigateToNextStep]);
    return { navigateToNextStep, startMonevoFlow, continueIntoFunnel };
};
export default useMonevoNavigation;
